import { api, type ChangeEmailRequest, type ChangeEmailResponse, type ChangePasswordRequest, type ChangePasswordResponse, type EmailVerificationResponse } from '../lib/api-client';
import { ok, readApiResult, type DataResult } from '../lib/data-result';
import type {
  UpdateProfileRequest,
  UpdateSettingsRequest,
  UserProfile,
  UserSettings,
} from '../types/api';

export interface UserSettingsPageData {
  profile: UserProfile;
  notificationSettings: UserSettings | null;
}

export interface ProfileFormValues {
  display_name: string;
  bio: string;
  location: string;
  website: string;
}

export interface ProfileFormState {
  readonly submitDisabled: boolean;
}

export interface EmailVerificationDisplay {
  readonly emailText: string;
  readonly statusText: string;
  readonly statusTone: 'verified' | 'required' | 'unverified';
  readonly summaryText: string;
  readonly alertVisible: boolean;
  readonly alertText: string;
  readonly verifyButtonVisible: boolean;
  readonly verifyButtonText: string;
}

export interface EmailVerificationRequestFeedback {
  readonly toastText: string;
  readonly statusText: string;
  readonly buttonText: string;
  readonly buttonDisabled: boolean;
  readonly reloadSettings: boolean;
}

export interface ChangeEmailSuccessFeedback {
  readonly toastText: string;
  readonly verificationFeedback?: EmailVerificationRequestFeedback;
}

export interface EmailChangeFormValues {
  readonly currentEmail: string;
  readonly newEmail: string;
  readonly password: string;
}

export interface EmailChangeFormState {
  readonly passwordRequired: boolean;
  readonly submitDisabled: boolean;
  readonly passwordHelpText: string;
}

export interface PasswordChangeFormValues {
  readonly oldPassword: string;
  readonly newPassword: string;
  readonly confirmPassword: string;
}

export interface PasswordChangeFormState {
  readonly submitDisabled: boolean;
}

export interface PasswordChangeSuccessFeedback {
  readonly toastText: string;
  readonly statusText: string;
}

export type SettingsSection = 'account' | 'profile' | 'notifications';

export type NotificationWatchSettings = Pick<UserSettings, 'watch_own_issues' | 'watch_own_submissions'>;

export interface NotificationSettingsFormState {
  readonly submitDisabled: boolean;
}

export type { ChangeEmailRequest, ChangeEmailResponse, ChangePasswordRequest, ChangePasswordResponse, EmailVerificationResponse } from '../lib/api-client';

export function shouldFocusEmailVerification(search: string): boolean {
  return new URLSearchParams(search).get('focus') === 'email-verification';
}

export function hasEmailVerificationSentHint(search: string): boolean {
  return new URLSearchParams(search).get('email_verification_sent') === '1';
}

function safeSameSitePath(value: string | null): string | null {
  const target = String(value || '').trim();
  if (!target || !target.startsWith('/') || target.startsWith('//')) return null;
  return target;
}

export function getEmailVerificationReturnTarget(search: string): string | null {
  const params = new URLSearchParams(search);
  return safeSameSitePath(params.get('next')) ?? safeSameSitePath(params.get('redirect'));
}

export function shouldShowEmailVerificationReturnAction(
  profile: Pick<UserProfile, 'email_verified_at' | 'email_verification_required'>,
  returnTarget: string | null,
): boolean {
  return Boolean(returnTarget && (profile.email_verified_at || profile.email_verification_required !== true));
}

export function consumeEmailVerificationFocusUrl(href: string): string {
  const url = new URL(href);
  url.search = buildSettingsSectionSearch(url.search, 'account');
  url.searchParams.delete('email_verification_sent');
  return url.toString();
}

export function resolveSettingsSection(search: string): SettingsSection {
  const params = new URLSearchParams(search);
  if (params.get('focus') === 'email-verification') return 'account';
  const section = params.get('section') || params.get('tab');
  if (section === 'profile' || section === 'notifications' || section === 'account') return section;
  return 'account';
}

export function buildSettingsSectionSearch(search: string, section: SettingsSection): string {
  const params = new URLSearchParams(search);
  params.delete('focus');
  params.delete('tab');
  if (section === 'account') {
    params.delete('section');
  } else {
    params.set('section', section);
  }
  const next = params.toString();
  return next ? `?${next}` : '';
}

export function buildProfileUpdates(original: UserProfile, current: ProfileFormValues): UpdateProfileRequest {
  return {
    ...(current.display_name !== (original.display_name || '') ? { display_name: current.display_name || null } : {}),
    ...(current.bio !== (original.bio || '') ? { bio: current.bio || null } : {}),
    ...(current.location !== (original.location || '') ? { location: current.location || null } : {}),
    ...(current.website !== (original.website || '') ? { website: current.website || null } : {}),
  };
}

export function buildProfileFormState(
  original: UserProfile | null,
  current: ProfileFormValues,
): ProfileFormState {
  return {
    submitDisabled: original === null || Object.keys(buildProfileUpdates(original, current)).length === 0,
  };
}

export function buildNotificationSettingsUpdates(
  original: NotificationWatchSettings | null,
  current: NotificationWatchSettings,
): UpdateSettingsRequest | null {
  if (
    original &&
    current.watch_own_issues === original.watch_own_issues &&
    current.watch_own_submissions === original.watch_own_submissions
  ) {
    return null;
  }
  return {
    watch_own_issues: current.watch_own_issues,
    watch_own_submissions: current.watch_own_submissions,
  };
}

export function buildNotificationSettingsFormState(
  original: NotificationWatchSettings | null,
  current: NotificationWatchSettings,
): NotificationSettingsFormState {
  return {
    submitDisabled: original === null || buildNotificationSettingsUpdates(original, current) === null,
  };
}

export function buildEmailVerificationDisplay(profile: Pick<UserProfile, 'email' | 'email_verified_at' | 'email_verification_required'>): EmailVerificationDisplay {
  if (profile.email_verified_at) {
    return {
      emailText: profile.email,
      statusText: 'Verified',
      statusTone: 'verified',
      summaryText: 'Your account email is verified.',
      alertVisible: false,
      alertText: '',
      verifyButtonVisible: false,
      verifyButtonText: 'Verified',
    };
  }
  if (profile.email_verification_required) {
    return {
      emailText: profile.email,
      statusText: 'Verification required',
      statusTone: 'required',
      summaryText: 'Verify this email to create agent tokens and keep account recovery available.',
      alertVisible: true,
      alertText: 'Verify your email to create agent tokens, reset your password safely, and keep account recovery available.',
      verifyButtonVisible: true,
      verifyButtonText: 'Send verification email',
    };
  }
  return {
    emailText: profile.email,
    statusText: 'Not verified',
    statusTone: 'unverified',
    summaryText: 'Verification is optional right now, but recommended for account recovery.',
    alertVisible: false,
    alertText: '',
    verifyButtonVisible: true,
    verifyButtonText: 'Send verification email',
  };
}

export function buildEmailVerificationRequestFeedback(result: EmailVerificationResponse): EmailVerificationRequestFeedback {
  if (result.already_verified) {
    return {
      toastText: 'Email is already verified.',
      statusText: 'Your account email is verified.',
      buttonText: 'Verified',
      buttonDisabled: true,
      reloadSettings: true,
    };
  }
  return {
    toastText: 'Verification email sent.',
    statusText: 'Verification email sent. Check your inbox, then return here after opening the link.',
    buttonText: 'Verification email sent',
    buttonDisabled: true,
    reloadSettings: false,
  };
}

export function buildChangeEmailSuccessMessage(result: ChangeEmailResponse): string {
  return buildChangeEmailSuccessFeedback(result).toastText;
}

export function buildChangeEmailSuccessFeedback(result: ChangeEmailResponse): ChangeEmailSuccessFeedback {
  if (result.unchanged) {
    return { toastText: 'Email is already set to this address.' };
  }
  if (result.verification_email_sent) {
    return {
      toastText: 'Email updated. Verification email sent.',
      verificationFeedback: buildEmailVerificationRequestFeedback({}),
    };
  }
  return { toastText: 'Email updated. Use Send verification email to verify the new address.' };
}

export function isEmailChangeUnchanged(currentEmail: string, newEmail: string): boolean {
  return currentEmail.trim().toLowerCase() === newEmail.trim().toLowerCase();
}

function isBasicEmailAddress(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
}

export function buildEmailChangeFormState(currentEmail: string, newEmail: string, password = ''): EmailChangeFormState {
  const normalizedCurrentEmail = currentEmail.trim();
  const normalizedNewEmail = newEmail.trim();
  if (!normalizedCurrentEmail || !normalizedNewEmail) {
    return {
      passwordRequired: false,
      submitDisabled: true,
      passwordHelpText: 'Enter a different email to enable email update.',
    };
  }
  if (!isBasicEmailAddress(normalizedNewEmail)) {
    return {
      passwordRequired: false,
      submitDisabled: true,
      passwordHelpText: 'Enter a valid email address.',
    };
  }
  if (isEmailChangeUnchanged(normalizedCurrentEmail, normalizedNewEmail)) {
    return {
      passwordRequired: false,
      submitDisabled: true,
      passwordHelpText: 'This is already your account email.',
    };
  }
  if (!password) {
    return {
      passwordRequired: true,
      submitDisabled: true,
      passwordHelpText: 'Enter your current password to enable email update.',
    };
  }
  return {
    passwordRequired: true,
    submitDisabled: false,
    passwordHelpText: 'Required to confirm this email change.',
  };
}

export function validateEmailChangeInput(values: EmailChangeFormValues): string | null {
  const newEmail = values.newEmail.trim();
  if (!newEmail) {
    return 'Enter the email address you want to use.';
  }
  if (!isBasicEmailAddress(newEmail)) {
    return 'Enter a valid email address.';
  }
  if (!isEmailChangeUnchanged(values.currentEmail, newEmail) && !values.password) {
    return 'Enter your current password.';
  }
  return null;
}

export function validatePasswordChangeInput(values: PasswordChangeFormValues): string | null {
  if (!values.oldPassword) {
    return 'Enter your current password.';
  }
  if (values.newPassword.length < 6) {
    return 'New password must be at least 6 characters.';
  }
  if (values.newPassword === values.oldPassword) {
    return 'New password must be different from current password.';
  }
  if (values.newPassword !== values.confirmPassword) {
    return 'New passwords do not match.';
  }
  return null;
}

export function buildPasswordChangeFormState(values: PasswordChangeFormValues): PasswordChangeFormState {
  return {
    submitDisabled: validatePasswordChangeInput(values) !== null,
  };
}

export function buildPasswordChangeFormHelpText(values: PasswordChangeFormValues): string {
  const started = values.oldPassword.length > 0 || values.newPassword.length > 0 || values.confirmPassword.length > 0;
  if (!started) return '';
  if (!values.oldPassword) return 'Enter your current password.';
  if (!values.newPassword) return 'Enter a new password.';
  if (values.newPassword.length < 6) return 'New password must be at least 6 characters.';
  if (values.newPassword === values.oldPassword) return 'New password must be different from current password.';
  if (!values.confirmPassword) return 'Confirm your new password.';
  if (values.confirmPassword.length < 6) return 'Confirm password must be at least 6 characters.';
  if (values.newPassword !== values.confirmPassword) return 'New passwords do not match.';
  return '';
}

export function buildPasswordChangeSuccessFeedback(result: ChangePasswordResponse): PasswordChangeSuccessFeedback {
  if (result.security_email_sent) {
    return {
      toastText: 'Password updated. Security notice sent.',
      statusText: 'Password updated. A security notice was sent to your account email.',
    };
  }
  return {
    toastText: 'Password updated.',
    statusText: 'Password updated. Security notice email was not sent, but your password was changed.',
  };
}

export async function loadSettingsData(): Promise<DataResult<UserSettingsPageData>> {
  const [profileResult, notificationResult] = await Promise.all([
    readApiResult(
      () => api.users.me(),
      'Failed to load settings',
      { networkError: 'Network error' },
    ),
    readApiResult(
      () => api.users.getNotificationSettings(),
      'Failed to load notification settings',
    ),
  ]);

  if (!profileResult.ok) return profileResult;

  return ok({
    profile: profileResult.data,
    notificationSettings: notificationResult.ok ? notificationResult.data : null,
  });
}

export async function updateProfileSettings(updates: UpdateProfileRequest): Promise<DataResult<UserProfile>> {
  return readApiResult(
    () => api.users.updateProfile(updates),
    'Failed to update profile',
    { networkError: 'Network error' },
  );
}

export async function updateNotificationSettings(updates: UpdateSettingsRequest): Promise<DataResult<{ updated: string[] }>> {
  return readApiResult(
    () => api.users.updateNotificationSettings(updates),
    'Failed to save notification settings',
    { networkError: 'Network error' },
  );
}

export async function requestEmailVerification(search = ''): Promise<DataResult<EmailVerificationResponse>> {
  const redirect = getEmailVerificationReturnTarget(search);
  return readApiResult(
    () => api.users.requestEmailVerification(redirect ? { redirect } : undefined),
    'Request failed',
    { networkError: 'Network error' },
  );
}

export async function changeEmail(request: ChangeEmailRequest, search = ''): Promise<DataResult<ChangeEmailResponse>> {
  const redirect = getEmailVerificationReturnTarget(search);
  return readApiResult(
    () => api.users.changeEmail(redirect ? { ...request, redirect } : request),
    'Request failed',
    { networkError: 'Network error' },
  );
}

export async function changePassword(request: ChangePasswordRequest): Promise<DataResult<ChangePasswordResponse>> {
  return readApiResult(
    () => api.users.changePassword(request),
    'Request failed',
    { networkError: 'Network error' },
  );
}

if (typeof window !== 'undefined') {
  window.UserSettingsData = {
    buildProfileUpdates,
    buildProfileFormState,
    buildNotificationSettingsUpdates,
    buildNotificationSettingsFormState,
    buildEmailVerificationDisplay,
    buildEmailVerificationRequestFeedback,
    buildChangeEmailSuccessFeedback,
    buildChangeEmailSuccessMessage,
    buildPasswordChangeSuccessFeedback,
    buildPasswordChangeFormHelpText,
    changeEmail,
    changePassword,
    loadSettingsData,
    requestEmailVerification,
    consumeEmailVerificationFocusUrl,
    getEmailVerificationReturnTarget,
    shouldShowEmailVerificationReturnAction,
    hasEmailVerificationSentHint,
    buildSettingsSectionSearch,
    resolveSettingsSection,
    shouldFocusEmailVerification,
    updateNotificationSettings,
    updateProfileSettings,
    buildEmailChangeFormState,
    buildPasswordChangeFormState,
    isEmailChangeUnchanged,
    validateEmailChangeInput,
    validatePasswordChangeInput,
  };
}
